import { useDispatch, useSelector } from "react-redux";
import { useRef, useMemo, useEffect } from "react";
import { fabric } from 'fabric';
import { Texture, TwoDMesh, actions as builderActions } from "@/store/builderSlice";
import { findEndpoints, getWallsFromVertices, intersectOfLines } from "@/utils/builder/twoDRoomLayoutUtils";
import { Point } from "fabric/fabric-impl";
import { DimensionalViewType } from "@/utils/builder/builderConstants";
import BuilderSelect from "@/store/builderStoreSelector";

export interface TwoDWallLayoutBuilderProps {
    canvas: fabric.Canvas;
    points: { x: number, y: number }[];
    adjustCanvasPosition: Function;
    unitsScale: number;
    wallColor: string;
    floorTextureId: string;
    xOffset: number;
    yOffset: number;
}

const TwoDWallLayoutBuilder: React.FC<TwoDWallLayoutBuilderProps> = ({ canvas, points, adjustCanvasPosition, unitsScale, wallColor, floorTextureId, xOffset, yOffset }
) => {
    const dispatch = useDispatch();
    const wallsPolygonFabObj = useRef<fabric.Polygon | undefined>(undefined);
    const urlParams: { [key: string]: string } = BuilderSelect.ui().urlParams;
    const isEditFlow = urlParams.projectId != null;
    const textures: Texture[] = BuilderSelect.designContent().textures;
    var img = new Image();
    img.src = './icons/roomCornerControl.png';

    let canAddObj = false;
    wallsPolygonFabObj.current = useMemo(() => {
        canAddObj = wallsPolygonFabObj.current === undefined;
        const polyPoints = points.map((point) => ({ x: point.x * unitsScale, y: point.y * unitsScale }));
        if (wallsPolygonFabObj.current && wallsPolygonFabObj.current.points!.length !== polyPoints.length) {
            canAddObj = true;
            canvas.remove(wallsPolygonFabObj.current);
        }
        const polygon = new fabric.Polygon(polyPoints, {
            name: 'roomPolygon',
            left: 0,
            top: 0,
            fill: 'white',
            strokeWidth: 10,
            //stroke: wallColor,
            scaleX: 1,
            scaleY: 1,
            objectCaching: false,
            transparentCorners: false,
            cornerColor: 'blue',
            lockMovementX: true,
            lockMovementY: true,
            lockRotation: true
        });

        if (!canAddObj) canvas.requestRenderAll();
        return polygon;
    }, [points.length]);

    const applyFloorTexture = () => {
        if (!canvas || !canvas.getContext()) {
            console.error("Canvas is not initialized or has no context");
            return;
        }
    
        const texture = textures.find(texture => texture.id === floorTextureId);
        if (texture && wallsPolygonFabObj.current) {
            fabric.util.loadImage(texture.textureUrl, function (img) {
                if (!img) {
                    console.error("Failed to load texture image");
                    return;
                }
    
                // Check if canvas is still valid before rendering
                if (!canvas || !canvas.getContext()) {
                    console.error("Canvas context lost before texture could be applied");
                    return;
                }
    
                wallsPolygonFabObj.current!.set('fill', new fabric.Pattern({
                    source: img,
                    repeat: 'repeat',
                    patternTransform: [0.15, 0, 0, 0.15, 0, 0],
                }));
                
                // Use requestRenderAll which is safer than renderAll
                try {
                    canvas.renderAll();
                } catch (error) {
                    console.error("Error rendering canvas:", error);
                }
            });
        }
    };
    

    // commented because it is preventing 2d buuilder from not applying floor texture in 3d

    useEffect(() => {
        // Only attempt to apply texture if canvas is available
        // if (canvas && canvas.getContext()) {
            applyFloorTexture();
        // }
    }, [floorTextureId]);

    function polygonPositionHandler(dim: any, finalMatrix: any, fabricObject: fabric.Polygon) {
        //@ts-ignore
        var x = (fabricObject.points![this.pointIndex].x - fabricObject.pathOffset.x),
            //@ts-ignore
            y = (fabricObject.points![this.pointIndex].y - fabricObject.pathOffset.y);
        return fabric.util.transformPoint(
            { x, y } as Point,
            fabric.util.multiplyTransformMatrices(
                fabricObject.canvas!.viewportTransform!,
                fabricObject.calcTransformMatrix()
            )
        );
    }

    function getObjectSizeWithStroke(object: fabric.Polygon) {
        var stroke = new fabric.Point(
            object.strokeUniform ? 1 / object.scaleX! : 1,
            object.strokeUniform ? 1 / object.scaleY! : 1
        ).multiply(object.strokeWidth!);
        return new fabric.Point(object.width! + stroke.x, object.height! + stroke.y);
    }

    function actionHandler(eventData: any, transform: any, x: number, y: number) {
        var polygon = transform.target,
            currentControl = polygon.controls[polygon.__corner],
            mouseLocalPosition = polygon.toLocalPoint(new fabric.Point(x, y), 'center', 'center'),
            polygonBaseSize = getObjectSizeWithStroke(polygon),
            size = polygon._getTransformedDimensions(0, 0),
            finalPointPosition = {
                x: mouseLocalPosition.x * polygonBaseSize.x / size.x + polygon.pathOffset.x,
                y: mouseLocalPosition.y * polygonBaseSize.y / size.y + polygon.pathOffset.y
            };

        const canvasWidth = canvas.width!;
        const canvasHeight = canvas.height!;
        const viewportTransformX = canvas.viewportTransform![4] || 0;
        const viewportTransformY = canvas.viewportTransform![5] || 0;
        const buffer = 30;
        const canvasTopOffset = 65;
        const { innerWidth: width, innerHeight: height } = window;
        const canvasMobileTopoffset = width < 768 ? 65 : 0;
        if (finalPointPosition.x < -viewportTransformX + buffer) finalPointPosition.x = -viewportTransformX + buffer;
        if (finalPointPosition.y < -viewportTransformY + buffer + canvasMobileTopoffset) finalPointPosition.y = -viewportTransformY + buffer + canvasMobileTopoffset;
        if (finalPointPosition.x + viewportTransformX + buffer > canvasWidth) finalPointPosition.x = canvasWidth - viewportTransformX - buffer;
        if (finalPointPosition.y + viewportTransformY + buffer + canvasTopOffset > canvasHeight) finalPointPosition.y = canvasHeight - viewportTransformY - buffer - canvasTopOffset;

        polygon.points[currentControl.pointIndex] = finalPointPosition;


        const vertices = polygon.points!.map((point: { x: number, y: number }) =>
            ({ x: point.x / unitsScale, y: point.y / unitsScale }));
        const centerPoint = {
            x: wallsPolygonFabObj.current!.getCenterPoint().x / unitsScale,
            y: wallsPolygonFabObj.current!.getCenterPoint().y / unitsScale
        }
        const orgBoundingRect = wallsPolygonFabObj.current!.getBoundingRect(false, true);
        const boundingRect = {
            left: orgBoundingRect.left / unitsScale,
            top: orgBoundingRect.top / unitsScale,
            width: orgBoundingRect.width / unitsScale,
            height: orgBoundingRect.height / unitsScale,

        }
        dispatch(builderActions.updateTwoDUi({
            vertices,
            centerPoint,
            boundingRect,
            walls: getWallsFromVertices(vertices)
        }));


        return true;
    }

    function anchorWrapper(anchorIndex: number, fn: Function) {
        return function (eventData: any, transform: any, x: number, y: number) {
            var fabricObject = transform.target,
                absolutePoint = fabric.util.transformPoint({
                    x: (fabricObject.points[anchorIndex].x - fabricObject.pathOffset.x),
                    y: (fabricObject.points[anchorIndex].y - fabricObject.pathOffset.y),
                } as Point, fabricObject.calcTransformMatrix()),
                actionPerformed = fn(eventData, transform, x, y),
                newDim = fabricObject._setPositionDimensions({}),
                polygonBaseSize = getObjectSizeWithStroke(fabricObject),
                newX = (fabricObject.points[anchorIndex].x - fabricObject.pathOffset.x) / polygonBaseSize.x,
                newY = (fabricObject.points[anchorIndex].y - fabricObject.pathOffset.y) / polygonBaseSize.y;
            fabricObject.setPositionByOrigin(absolutePoint, newX + 0.5, newY + 0.5);
            return actionPerformed;
        }
    }

    function Edit() {
        var poly = wallsPolygonFabObj.current;
        if (!poly) return;
        canvas.setActiveObject(poly);
        //@ts-ignore
        poly.edit = true;//!poly.edit;
        //@ts-ignore
        if (wallsPolygonFabObj.current && poly.edit) {
            var lastControl = poly.points!.length - 1;
            poly.cornerStyle = 'circle';
            poly.cornerColor = 'rgba(0,0,255,0.5)';
            poly.controls = poly.points!.reduce(function (acc: { [key: string]: fabric.Control }, point, index: number) {
                acc['p' + index] = new fabric.Control({
                    positionHandler: polygonPositionHandler,
                    actionHandler: anchorWrapper(index > 0 ? index - 1 : lastControl, actionHandler),
                    actionName: 'modifyPolygon',
                    //@ts-ignore
                    pointIndex: index,
                    render: (ctx, left, top, styleOverride, fabricObject) => {
                        const size = 20;
                        ctx.save();
                        ctx.translate(left, top);
                        ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle!));
                        ctx.drawImage(img, -size / 2, -size / 2, size, size);
                        ctx.restore();
                    }
                });
                return acc;
            }, {});
        } else {
            poly.cornerColor = 'blue';
            poly.cornerStyle = 'rect';
            poly.controls = fabric.Object.prototype.controls;
        }
        //@ts-ignore
        poly.hasBorders = !poly.edit;
        try {
            canvas.requestRenderAll();
        } catch (error) {
            console.error("Error rendering canvas during Edit:", error);
        }
    }

    const onWallLayoutChange = () => {
        console.log('canvas- onWallLayoutChange :: ', wallsPolygonFabObj.current!.points);
        const vertices = wallsPolygonFabObj.current!.points!.map((point) =>
            ({ x: point.x / unitsScale, y: point.y / unitsScale }));
        let minX = Infinity, minY = Infinity;
        vertices.forEach(vertex => {
            if (vertex.x < minX) minX = vertex.x;
            if (vertex.y < minY) minY = vertex.y;
        });
        const centerPoint = {
            x: wallsPolygonFabObj.current!.getCenterPoint().x / unitsScale - (minX - xOffset),
            y: wallsPolygonFabObj.current!.getCenterPoint().y / unitsScale - (minY - yOffset)
        }
        const orgBoundingRect = wallsPolygonFabObj.current!.getBoundingRect(false, true);
        const boundingRect = {
            left: orgBoundingRect.left / unitsScale,
            top: orgBoundingRect.top / unitsScale,
            width: orgBoundingRect.width / unitsScale,
            height: orgBoundingRect.height / unitsScale,

        }
        dispatch(builderActions.updateTwoDLayoutVertices({
            vertices,
            centerPoint,
            boundingRect,
            undoable: true
        }));
    }
    const initEvents = () => {
        if (!wallsPolygonFabObj.current) return;
        wallsPolygonFabObj.current.on('modified', onWallLayoutChange);
    }

    if (canAddObj && canvas && canvas.getContext()) {
        try {
            canvas.insertAt(wallsPolygonFabObj.current, 1, false);
            Edit();
            applyFloorTexture();
            canvas.requestRenderAll();
            initEvents();
            const vertices = wallsPolygonFabObj.current!.points!.map((point) =>
                ({ x: point.x / unitsScale, y: point.y / unitsScale }));
            let minX = Infinity, minY = Infinity;
            vertices.forEach(vertex => {
                if (vertex.x < minX) minX = vertex.x;
                if (vertex.y < minY) minY = vertex.y;
            });
            const centerPoint = {
                x: wallsPolygonFabObj.current!.getCenterPoint().x / unitsScale - (minX - xOffset),
                y: wallsPolygonFabObj.current!.getCenterPoint().y / unitsScale - (minY - yOffset)
            }
            const orgBoundingRect = wallsPolygonFabObj.current!.getBoundingRect(false, true);
            const boundingRect = {
                left: orgBoundingRect.left / unitsScale,
                top: orgBoundingRect.top / unitsScale,
                width: orgBoundingRect.width / unitsScale,
                height: orgBoundingRect.height / unitsScale,

            }
            dispatch(builderActions.updateTwoDLayoutVertices({
                vertices,
                centerPoint,
                boundingRect
            }));
            (!urlParams.createRoom || isEditFlow) && dispatch(builderActions.updateLayoutBuilderMode({ layoutBuilderMode: DimensionalViewType.THREE_D }));
            const roomBoundingRect = wallsPolygonFabObj.current.getBoundingRect();
            adjustCanvasPosition(roomBoundingRect);
        } catch (error) {
            console.error("Error during initial canvas setup:", error);
        }
    }

    if (wallsPolygonFabObj.current && canvas && canvas.getContext()) {
        try {
            const polyPoints = points.map((point) => ({ x: point.x * unitsScale, y: point.y * unitsScale }));
            wallsPolygonFabObj.current.points! = polyPoints as Point[];
            //wallsPolygonFabObj.current.stroke = wallColor;
            if (wallsPolygonFabObj.current.points.length !== polyPoints.length)
                Edit();
            canvas.requestRenderAll();
            console.log('canvas- center of poly :: ', wallsPolygonFabObj.current.getBoundingRect(false, true), wallsPolygonFabObj.current.getCenterPoint());
        } catch (error) {
            console.error("Error updating polygon points:", error);
        }
    }
    return <></>;
}

export default TwoDWallLayoutBuilder;